// WxHazardBar — bottom-of-map control surface for the AIRMET / SIGMET /
// CWA family. Two visually-separate floating cards centered over the
// lower part of the map; both appear only when the master 'wxhazards'
// layer is on. They sit above the instrument bar (when visible) so
// they never overlap it.
//
// Layout (bottom of map area, vertical stack from bottom up):
//
//   ┌──────────────────────────────────────────────────────────┐
//   │           [scrubber card: time slider]                   │
//   ├──────────────────────────────────────────────────────────┤   (gap)
//   │   [hazard card:  AIRMET ▾   SIGMET   CWA]                │
//   │   (when expanded:  IFR  Mtn  Icing  Turb…  LLWS  FZ  Sfc)│
//   ├──────────────────────────────────────────────────────────┤   (gap)
//   │           [instrument bar — when visible]                │
//   └──────────────────────────────────────────────────────────┘
//
// Both cards center themselves; pointer events scoped to the cards
// only so the surrounding map stays interactive.
//
// State:
//   * Master toggle drives visibility.
//   * Family chips (AIRMET / SIGMET / CWA) toggle the per-family
//     layer (or, for AIRMET, the group of 8 sub-hazards).
//   * AIRMET chevron expands a sub-row of 8 hazard chips for fine
//     control.
//   * Time slider drives wxHazardTime in the store. Range
//     -3h .. +9h in 1h steps, default 0 (= "now"). MapView reads it
//     and rebuilds GFA layer filters so polygons whose validTime is
//     within ±3h of (now + offset) render. SIGMETs / CWAs use their
//     own validTimeFrom / validTimeTo overlap with the slider window.

import { useState } from 'react';
import { useUiStore } from '../stores/uiStore';
import { useLayerVisibilityStore } from '../stores/layerVisibilityStore';
import { useInstrumentPrefsStore } from '../stores/instrumentPrefsStore';
import { useInstrumentStripHeight } from './useInstrumentStripHeight';
import type { LayerId } from './layerCatalog';

const GFA_HAZARDS: Array<{ id: LayerId; label: string; color: string }> = [
  { id: 'gfa-ifr',      label: 'IFR',       color: '#60a5fa' },
  { id: 'gfa-mtnobscn', label: 'Mtn Obsc',  color: '#a855f7' },
  { id: 'gfa-ice',      label: 'Icing',     color: '#7dd3fc' },
  { id: 'gfa-turblo',   label: 'Turb Lo',   color: '#fb923c' },
  { id: 'gfa-turbhi',   label: 'Turb Hi',   color: '#fb923c' },
  { id: 'gfa-llws',     label: 'LLWS',      color: '#facc15' },
  { id: 'gfa-fzlvl',    label: 'FZ Lvl',    color: '#0284c7' },
  { id: 'gfa-sfcwnd',   label: 'Sfc Wind',  color: '#facc15' },
];

// Slider range: -3h ago through +6h ahead, 1h steps. Bounded by
// what AWC actually publishes for G-AIRMETs: one previous issuance
// (3h ago) plus the current issuance's forecast frames at +0/+3/+6.
// Boss feedback 2026-05-10: capping at +6 keeps the slider honest —
// past +6 there's no AWC-published frame to show.
const SLIDER_MIN = -3;
const SLIDER_MAX = 6;
const SLIDER_STEP = 1;

export default function WxHazardBar() {
  const showWxHazards = useLayerVisibilityStore((s) => s.layers.wxhazards);
  const showInstruments = useInstrumentPrefsStore((s) => s.showInstruments);
  const layers = useLayerVisibilityStore((s) => s.layers);
  const toggleLayer = useLayerVisibilityStore((s) => s.toggleLayer);
  const wxHazardOffsetH = useUiStore((s) => s.wxHazardOffsetH);
  const setWxHazardOffsetH = useUiStore((s) => s.setWxHazardOffsetH);

  const [airmetExpanded, setAirmetExpanded] = useState<boolean>(false);
  // Live height of the bottom instrument strip so this bar can stack
  // ABOVE it without overlap. Hook short-circuits when this bar isn't
  // visible so we don't waste a ResizeObserver.
  const instrumentH = useInstrumentStripHeight(showWxHazards);

  if (!showWxHazards) return null;

  // Active counts (for the AIRMET chip's badge).
  const gfaOnCount = GFA_HAZARDS.filter((h) => layers[h.id]).length;
  const anyGfaOn = gfaOnCount > 0;

  // Tap the AIRMET chip BODY -> toggle the group (all 8 on if any off,
  // else all 8 off). Tap the chevron -> expand the sub-row only.
  const toggleAirmetGroup = () => {
    const next = !anyGfaOn;
    for (const h of GFA_HAZARDS) toggleLayer(h.id, next);
  };

  // Vertical positioning. Both cards stack from the bottom up,
  // measured up from the instrument bar's actual rendered height. On
  // desktop the bar is one row (~52px); on narrow phones it wraps to
  // two rows (~90px). When instruments are hidden we stack against
  // the bottom of the map with a small gutter.
  const INSTRUMENT_BOTTOM_OFFSET = 12;        // bottom-3 from MapPage
  // Generous gap above the instrument bar — 12px reads as a real
  // visual separation on phones, where 6px looks like 'these touch'.
  const ABOVE_INSTRUMENTS_GAP_PX = 14;
  // Gap between hazard and scrubber cards (smaller — they share an
  // affordance group and read as a stack).
  const CARD_GAP_PX = 8;
  const baseBottomPx = showInstruments
    ? INSTRUMENT_BOTTOM_OFFSET + instrumentH + ABOVE_INSTRUMENTS_GAP_PX
    : 14;
  const scrubberHeightPx = 56;                // matches the card's px-3 py-2 + content
  const hazardCardBottomPx = baseBottomPx + scrubberHeightPx + CARD_GAP_PX;

  return (
    <>
      {/* HAZARD CARD \u2014 centered horizontally, sits above the scrubber */}
      <div
        className="absolute left-0 right-0 z-10 flex justify-center pointer-events-none"
        style={{ bottom: `${hazardCardBottomPx}px` }}
      >
        <div className="pointer-events-auto bg-cockpit-800/95 backdrop-blur border border-cockpit-600 rounded-xl shadow-bar p-2 max-w-[calc(100vw-1.5rem)]">
          {/* Row 1 \u2014 hazard family chips */}
          <div className="flex items-center justify-center gap-1.5 flex-wrap">
            <FamilyChip
              label="AIRMET"
              sub={anyGfaOn ? `${gfaOnCount}/8` : 'off'}
              active={anyGfaOn}
              onTap={toggleAirmetGroup}
              onExpand={() => setAirmetExpanded((v) => !v)}
              expanded={airmetExpanded}
            />
            <FamilyChip
              label="SIGMET"
              active={layers.sigmet}
              onTap={() => toggleLayer('sigmet')}
            />
            <FamilyChip
              label="CWA"
              active={layers.cwa}
              onTap={() => toggleLayer('cwa')}
            />
          </div>

          {/* Row 2 \u2014 AIRMET sub-hazard chips, only when expanded */}
          {airmetExpanded && (
            <div className="flex items-center justify-center gap-1.5 flex-wrap pt-2 mt-1.5 border-t border-cockpit-700">
              {GFA_HAZARDS.map((h) => (
                <SubChip
                  key={h.id}
                  label={h.label}
                  color={h.color}
                  active={layers[h.id]}
                  onTap={() => toggleLayer(h.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* TIME SCRUBBER CARD \u2014 separate card, centered, below hazard card */}
      <div
        className="absolute left-0 right-0 z-10 flex justify-center pointer-events-none"
        style={{ bottom: `${baseBottomPx}px` }}
      >
        <div className="pointer-events-auto bg-cockpit-800/95 backdrop-blur border border-cockpit-600 rounded-xl shadow-bar px-3 py-2 w-[min(28rem,calc(100vw-1.5rem))]">
          <TimeSlider value={wxHazardOffsetH} onChange={setWxHazardOffsetH} />
        </div>
      </div>
    </>
  );
}

function FamilyChip({
  label,
  sub,
  active,
  onTap,
  onExpand,
  expanded,
}: {
  label: string;
  sub?: string;
  active: boolean;
  onTap: () => void;
  onExpand?: () => void;
  expanded?: boolean;
}) {
  return (
    <div
      className={[
        'inline-flex items-stretch rounded-md border overflow-hidden text-[11px] tracking-wide',
        active
          ? 'border-ember-500 bg-ember-600/20'
          : 'border-cockpit-600 bg-cockpit-700',
      ].join(' ')}
    >
      <button
        onClick={onTap}
        className={[
          'px-3 py-1.5 active:bg-cockpit-600 transition-colors flex items-center gap-1.5',
          active ? 'text-ember-200' : 'text-cockpit-200',
        ].join(' ')}
      >
        <span className="font-semibold">{label}</span>
        {sub && (
          <span className={active ? 'text-ember-300/80 text-[9px]' : 'text-cockpit-400 text-[9px]'}>
            {sub}
          </span>
        )}
      </button>
      {onExpand && (
        <button
          onClick={onExpand}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${label} hazards`}
          className={[
            'px-1.5 border-l flex items-center justify-center transition-colors',
            active
              ? 'border-ember-500/60 text-ember-200 active:bg-ember-600/30'
              : 'border-cockpit-600 text-cockpit-300 active:bg-cockpit-600',
          ].join(' ')}
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 120ms' }}
          >
            <path d="M2 4 L5 7 L8 4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
    </div>
  );
}

function SubChip({
  label,
  color,
  active,
  onTap,
}: {
  label: string;
  color: string;
  active: boolean;
  onTap: () => void;
}) {
  return (
    <button
      onClick={onTap}
      className={[
        'inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-[10.5px] tracking-wide transition-colors',
        active
          ? 'border-cockpit-500 bg-cockpit-700 text-cockpit-100'
          : 'border-cockpit-700 bg-cockpit-800/60 text-cockpit-400',
      ].join(' ')}
    >
      <span
        className="inline-block h-2 w-2 rounded-full"
        style={{
          backgroundColor: active ? color : 'transparent',
          border: `1.5px solid ${color}`,
          opacity: active ? 1 : 0.55,
        }}
        aria-hidden
      />
      {label}
    </button>
  );
}

/** Real slider for the wall-clock forecast offset. Center of the
 *  slider is "now" (offset 0). Drag left to look at the issuance
 *  that's already aged, drag right to look at the next +3 / +6 / +9
 *  hour forecast horizon. */
function TimeSlider({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  const labelText = formatOffsetLabel(value);
  const validAt = wallClockAt(value);

  // "now" tick label sits at the percentage position corresponding
  // to slider value 0 — not at 50% (which is what justify-between
  // would do for a 3-element row). Same for the min/max ticks; we
  // place all three by absolute percentage so the labels under the
  // track always line up with their actual values on the slider.
  const span = SLIDER_MAX - SLIDER_MIN;
  const pctNow = ((0 - SLIDER_MIN) / span) * 100;
  const pctMin = 0;
  const pctMax = 100;
  return (
    <div className="flex items-center gap-3">
      <div className="flex flex-col items-start min-w-[5.25rem]">
        <span className="text-[9px] tracking-widest text-cockpit-400 uppercase">Time</span>
        <span className="font-mono text-[12px] text-ember-300 leading-none mt-0.5">
          {labelText}
        </span>
      </div>
      <div className="flex-1">
        <input
          type="range"
          min={SLIDER_MIN}
          max={SLIDER_MAX}
          step={SLIDER_STEP}
          value={value}
          onChange={(e) => onChange(parseInt(e.currentTarget.value, 10))}
          aria-label="Hazard forecast time offset"
          className="w-full accent-ember-500"
        />
        <div className="relative h-3 mt-0.5 text-[9px] font-mono text-cockpit-400">
          <span className="absolute -translate-x-1/2" style={{ left: `${pctMin}%`, transform: 'translateX(0)' }}>
            {SLIDER_MIN}h
          </span>
          <span className="absolute -translate-x-1/2" style={{ left: `${pctNow}%` }}>
            now
          </span>
          <span className="absolute -translate-x-full" style={{ left: '100%' }}>
            +{SLIDER_MAX}h
          </span>
        </div>
      </div>
      <div className="text-right min-w-[3.75rem]">
        <span className="text-[9px] tracking-widest text-cockpit-400 uppercase">Valid</span>
        <div className="font-mono text-[11px] text-cockpit-100 leading-none mt-0.5">{validAt}</div>
      </div>
    </div>
  );
}

function formatOffsetLabel(offsetH: number): string {
  if (offsetH === 0) return 'now';
  if (offsetH > 0) return `+${offsetH}h`;
  return `${offsetH}h`;
}

/** "HH:MMZ" wall-clock for `now + offsetH` hours. */
function wallClockAt(offsetH: number): string {
  const t = new Date(Date.now() + offsetH * 3_600_000);
  const hh = String(t.getUTCHours()).padStart(2, '0');
  const mm = String(t.getUTCMinutes()).padStart(2, '0');
  return `${hh}:${mm}Z`;
}
